<template>
  <div
    ref="pdf"
    class="pdf-file"
  >
    <div
      v-for="page in pageTotal"
      :key="page"
      class="pdf-page"
      :class="{ debug: !working }"
    >
      <div class="header">
        <div class="title">
          {{ $t('favorites.title') }} - {{ $t('favorites.category.' + activeTab) }}
        </div>
      </div>
      <div class="content">
        <div class="items">
          <div
            v-for="(chunk, i) in chunks[page - 1]"
            :key="i"
            class="item"
          >
            <div class="title">
              {{ chunk.name }}
            </div>
            <div class="thumbnail">
              <img
                v-if="chunk.image"
                :src="chunk.image"
              >
              <default-logo
                v-else
                class="item-image"
              />
            </div>
            <div class="info">
              <div class="description">
                {{ slice(chunk.description, 500) || 'N/A' }}
              </div>
              <div class="note">
                <strong>{{ $t('favorites.pdf.note') }}</strong>
                {{ slice(chunk.note, 200) || 'N/A' }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="pager">
        {{ $t('favorites.pdf.pager', { page, total: pageTotal }) }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import _ from 'lodash'
import jsPDF from 'jspdf/dist/jspdf.es'
import html2canvas from 'html2canvas'
import DefaultLogo from './DefaultLogo'

export default {
  name: 'FavoritesPdf',
  components: {
    DefaultLogo
  },
  props: {
    activeTab: {
      type: String,
      default: ''
    },
    category: {
      type: String,
      default: ''
    },
    data: {
      type: Array,
      default: () => []
    }
  },
  data: () => ({
    working: false
  }),
  computed: {
    ...mapState('auth', [
      'name'
    ]),
    chunks () {
      const perPage = 3
      const data = _.cloneDeep(this.data || [])
      const chunkCount = Math.ceil(data.length / perPage)
      const result = []
      for (let i = 0; i < chunkCount; i++) {
        result.push(data.splice(0, perPage))
      }
      return result
    },
    pageTotal () {
      return this.chunks.length
    }
  },
  mounted () {
    this.download()
  },
  methods: {
    async download () {
      if (this.working) {
        return
      }
      this.working = true

      await this.$nextTick()

      // A4 in 96dpi
      // 29.7cm x 21cm
      const pageWidth = 1123
      const pageHeight = 794
      const prefix = this.$t('favorites.title') + '_' + this.$t('favorites.category.' + this.activeTab)
      const filename = `${prefix}_${this.$moment().format('YYYYMMDD')}.pdf`

      // eslint-disable-next-line new-cap
      const doc = new jsPDF({
        orientation: 'l',
        unit: 'px',
        hotfixes: ['px_scaling'],
        format: [pageWidth, pageHeight],
        compressPdf: true
      })

      const canvas = await html2canvas(this.$refs.pdf, {
        allowTaint: true,
        useCORS: true,
        width: pageWidth,
        y: 0
      })
      const img = canvas.toDataURL('image/jpeg', 0.8)
      const totalHeight = pageHeight * this.pageTotal
      for (let i = 0; i < this.pageTotal; i++) {
        doc.addImage(img, 'JPEG', 0, -pageHeight * i, pageWidth, totalHeight, undefined, 'FAST')
        if (i < this.pageTotal - 1) {
          doc.addPage()
        }
      }
      await doc.save(filename, {
        returnPromise: true
      })

      this.working = false
      this.$emit('export')
    },
    slice (string = '', length = 50, ellipsis = ' ...') {
      return string.length > length
        ? string.slice(0, length) + ellipsis
        : string
    }
  }
}
</script>

<style lang="stylus" scoped>
// Constants
$pdf-w = 1123px // A4 in 96dpi
$pdf-h = 794px
$pdf-p-x = 48px // 1.27cm
$pdf-p-y = 30px
$grey = #666

.pdf-file {
  width: $pdf-w

  // Hide from view
  position: absolute
  top: 0
  left: 100%
}
.pdf-page {
  width: $pdf-w
  height: $pdf-h
  padding: $pdf-p-y $pdf-p-x
  display: flex
  flex-direction: column
  font-family: Arial
  background: #FFF
  color: #000
  border: 1px solid transparent
  box-sizing: border-box

  &.debug {
    border-color: #000
  }
}
.header {
  display: flex
  justify-content: center
  align-items: center

  .title {
    font-size: 18px
    font-weight: bold
  }
}
.content {
  margin-top: 20px
  flex: 1

  .items {
    display: grid
    grid-template-columns: repeat(3, 1fr)
    grid-column-gap: 10px
    height: 100%
  }
  .item {
    display: flex
    flex-direction: column
    height: 100%
    overflow: hidden
  }
  .title {
    text-align: center
    margin-bottom: 10px
    line-height: 1
    font-size: 18px
    height: 36px // preserve two lines
  }
  .thumbnail {
    display: flex
    justify-content: center
    align-items: center
    height: 150px
    background: #efefef

    img {
      display: block
      max-width: 100%
      max-height: 100%
    }
  }
  .info {
    margin-top: 10px
    font-size: 13px
    display: flex
    flex-direction: column
    flex: 1
    line-height: 15px

    div:not(:first-child) {
      margin-top: 5px
    }
  }
  .description {
    flex: 1
    word-break: break-all
  }
  .note {
    word-break: break-all
  }
  strong {
    font-weight: bold
  }
}
.pager {
  text-align: right
  font-size: 12px
  margin-top: 10px
  color: $grey
}
</style>
